/**
 * PaymentEventHandlers.js
 *
 * Event binding and handling for payment modal interactions.
 * Manages user inputs, quantity controls, and payment actions.
 */

export class PaymentEventHandlers {
  constructor(modal) {
    this.modal = modal
  }

  /**
   * Bind all event handlers
   */
  bindEvents() {
    this.bindCloseEvents()
    this.bindPaymentMethodSelection()
    this.bindBackButton()
    this.bindPaymentActions()

    if (this.modal.currentPaymentMethod === 'blik') {
      this.bindBLIKKeypad()
    }

    if (this.modal.beforeOrder) {
      this.bindQuantityControls()
    }
  }

  /**
   * Bind close button and overlay click
   */
  bindCloseEvents() {
    const closeBtn = document.getElementById('unified-payment-close')
    const overlay = document.querySelector('.unified-payment-overlay')

    if (closeBtn) closeBtn.addEventListener('click', () => this.modal.hide())
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.modal.hide()
        }
      })
    }
  }

  /**
   * Bind payment method selection
   */
  bindPaymentMethodSelection() {
    document.querySelectorAll('.payment-method, .option-card').forEach(element => {
      element.addEventListener('click', (e) => {
        const method = e.currentTarget.getAttribute('data-method')
        this.modal.selectPaymentMethod(method)
      })
    })
  }

  /**
   * Bind back button to return to method selection
   */
  bindBackButton() {
    const backBtn = document.getElementById('payment-back-btn')
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        this.modal.currentPaymentMethod = null
        this.modal.refreshPaymentView()
      })
    }
  }

  /**
   * Bind payment action buttons
   */
  bindPaymentActions() {
    const submitBlik = document.getElementById('submit-blik-code')
    if (submitBlik) {
      submitBlik.addEventListener('click', () => this.modal.processBLIKPayment())
    }

    document.querySelectorAll('[id*="cancel"]').forEach(btn => {
      btn.addEventListener('click', () => this.modal.hide())
    })

    const confirmFallback = document.getElementById('confirm-fallback-payment')
    if (confirmFallback) {
      confirmFallback.addEventListener('click', () => this.modal.processFallbackPayment())
    }
  }

  /**
   * Bind BLIK keypad input
   */
  bindBLIKKeypad() {
    let blikCode = ''
    const codeDisplay = document.getElementById('blik-code-display')
    const submitBtn = document.getElementById('submit-blik-code')
    const emailInput = document.getElementById('blik-contact-email')
    const phoneInput = document.getElementById('blik-contact-phone')
    const consentInput = document.getElementById('blik-digital-receipt-consent')

    const pushPref = () => {
      const pref = {
        email: (emailInput?.value || '').trim(),
        phone: (phoneInput?.value || '').trim(),
        consent: !!consentInput?.checked,
      }
      try { this.modal.paymentService?.setDigitalReceiptPreference?.(pref) } catch {}
    }
    ;[emailInput, phoneInput, consentInput].forEach(el => el && el.addEventListener('input', pushPref))

    document.querySelectorAll('.keypad-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const key = e.target.getAttribute('data-key')

        if (key === 'C') {
          blikCode = ''
        } else if (key === '⌫') {
          blikCode = blikCode.slice(0, -1)
        } else if (blikCode.length < 6 && !isNaN(key)) {
          blikCode += key
        }

        const digits = codeDisplay.querySelectorAll('.code-digit')
        digits.forEach((digit, index) => {
          digit.textContent = blikCode[index] || '-'
        })

        this.modal.currentBlikCode = blikCode
        if (submitBtn) {
          submitBtn.disabled = blikCode.length !== 6
        }
      })
    })
  }

  /**
   * Bind quantity controls for cart items
   */
  bindQuantityControls() {
    document.querySelectorAll('#unified-payment-modal .order-item .item-actions').forEach(actions => {
      const container = actions.closest('.order-item')
      const idx = parseInt(container?.getAttribute('data-index') || '-1', 10)
      const cartId = actions.getAttribute('data-cart-id') || container?.getAttribute('data-cart-id')

      const updateLocal = (nextQty, remove = false) => {
        if (!Array.isArray(this.modal.currentOrder?.items)) return
        const items = [...this.modal.currentOrder.items]
        if (idx < 0 || idx >= items.length) return
        if (remove || nextQty <= 0) {
          items.splice(idx, 1)
        } else {
          items[idx] = { ...items[idx], quantity: nextQty, totalPrice: undefined }
        }
        const totals = { total: items.reduce((s, it) => s + (this.modal.state.getItemTotalPrice(it) || 0), 0) }
        this.modal.updateFromCart(items, totals, this.modal.currentOrder?.currency)
      }

      const updateBeforeOrder = (nextQty, remove = false) => {
        if (remove) {
          const evt = new CustomEvent('cartRemoveItem', { detail: { cartId } })
          document.dispatchEvent(evt)
        } else {
          const evt = new CustomEvent('cartUpdateQuantity', { detail: { cartId, quantity: nextQty } })
          document.dispatchEvent(evt)
        }
      }

      actions.querySelector('.increase-qty')?.addEventListener('click', () => {
        const qtyEl = actions.querySelector('.qty-value')
        const current = parseInt(qtyEl?.textContent || '1', 10)
        if (this.modal.beforeOrder) updateBeforeOrder(current + 1)
        else updateLocal(current + 1)
      })

      actions.querySelector('.decrease-qty')?.addEventListener('click', () => {
        const qtyEl = actions.querySelector('.qty-value')
        const current = parseInt(qtyEl?.textContent || '1', 10)
        if (this.modal.beforeOrder) updateBeforeOrder(current - 1)
        else updateLocal(current - 1)
      })

      actions.querySelector('.remove-item')?.addEventListener('click', () => {
        if (this.modal.beforeOrder) updateBeforeOrder(0, true)
        else updateLocal(0, true)
      })
    })
  }
}
